import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCw, CheckCircle, XCircle, Clock, Send,
  ShieldOff, Filter, Eye, MailPlus, AlertCircle, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/new-components/ui/select';
import api from '../../services/api';
import toast from 'react-hot-toast';

/* ── Types ──────────────────────────────────────────────────────── */
interface AccessRequest {
  id: string;
  user_id: string;
  deal_id: string;
  status: string;
  request_message: string | null;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  invest_link_token: string | null;
  invest_link_expires_at: string | null;
  invest_link_sent_at: string | null;
  created_at: string;
  updated_at: string;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  deal_name: string;
  deal_slug: string;
}

/* ── Helpers ────────────────────────────────────────────────────── */
const unwrap = (res: any) => {
  if (res?.data?.success !== undefined) return res.data;
  if (res?.success !== undefined) return res;
  return res?.data || res;
};

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const fmtDateTime = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'pending':
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    case 'approved':
      return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
    case 'denied':
      return <Badge className="bg-red-100 text-red-800 border-red-200"><XCircle className="w-3 h-3 mr-1" />Denied</Badge>;
    case 'expired':
      return <Badge className="bg-gray-100 text-gray-600 border-gray-200"><AlertCircle className="w-3 h-3 mr-1" />Expired</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
};

/* ── Component ──────────────────────────────────────────────────── */
const AdminAccessRequests: React.FC = () => {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');

  // Action dialog
  const [actionRequest, setActionRequest] = useState<AccessRequest | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'deny' | 'resend' | 'view' | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({ pending: 0, approved: 0, denied: 0, expired: 0 });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = unwrap(await api.get('/offering-access/admin/requests', {
        params: { status: statusFilter === 'all' ? undefined : statusFilter },
      }));
      if (res.success) {
        let data = res.data || [];
        // Client-side search filter
        if (search) {
          const q = search.toLowerCase();
          data = data.filter((r: AccessRequest) =>
            r.user_email?.toLowerCase().includes(q) ||
            `${r.user_first_name} ${r.user_last_name}`.toLowerCase().includes(q) ||
            r.deal_name?.toLowerCase().includes(q)
          );
        }
        setRequests(data);
        setTotal(data.length);
      }
    } catch (err) {
      toast.error('Failed to load access requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  // Compute stats from all requests (fetch all once)
  const fetchStats = useCallback(async () => {
    try {
      const res = unwrap(await api.get('/offering-access/admin/requests', {
        params: { status: undefined },
      }));
      if (res.success) {
        const data = res.data || [];
        setStats({
          pending: data.filter((r: AccessRequest) => r.status === 'pending').length,
          approved: data.filter((r: AccessRequest) => r.status === 'approved').length,
          denied: data.filter((r: AccessRequest) => r.status === 'denied').length,
          expired: data.filter((r: AccessRequest) => r.status === 'expired').length,
        });
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleAction = async () => {
    if (!actionRequest || !actionType || actionType === 'view') return;
    setActionLoading(true);
    try {
      const endpoint = actionType === 'resend'
        ? `/offering-access/admin/requests/${actionRequest.id}/resend`
        : `/offering-access/admin/requests/${actionRequest.id}/${actionType}`;
      
      const body: any = {};
      if (adminNotes) body.adminNotes = adminNotes;

      const res = unwrap(await api.post(endpoint, body));
      if (res.success) {
        toast.success(
          actionType === 'approve' ? 'Access approved — invest link sent!' :
          actionType === 'deny' ? 'Access request denied.' :
          'Invest link resent!'
        );
        setActionRequest(null);
        setActionType(null);
        setAdminNotes('');
        fetchRequests();
        fetchStats();
      } else {
        toast.error(res.error || 'Action failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openAction = (req: AccessRequest, type: 'approve' | 'deny' | 'resend' | 'view') => {
    setActionRequest(req);
    setActionType(type);
    setAdminNotes('');
  };

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldOff className="w-6 h-6 text-amber-600" />
            Access Requests
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Review and manage investment access requests from non-accredited investors
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchRequests(); fetchStats(); }} className="w-full sm:w-auto">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Pending', count: stats.pending, color: 'text-amber-600', bg: 'bg-amber-50', icon: <Clock className="w-5 h-5" /> },
          { label: 'Approved', count: stats.approved, color: 'text-green-600', bg: 'bg-green-50', icon: <CheckCircle className="w-5 h-5" /> },
          { label: 'Denied', count: stats.denied, color: 'text-red-600', bg: 'bg-red-50', icon: <XCircle className="w-5 h-5" /> },
          { label: 'Expired', count: stats.expired, color: 'text-gray-500', bg: 'bg-gray-50', icon: <AlertCircle className="w-5 h-5" /> },
        ].map(s => (
          <Card key={s.label} className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setStatusFilter(s.label.toLowerCase())}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`${s.bg} ${s.color} p-2 rounded-lg`}>{s.icon}</div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{s.count}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, or offering..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <Filter className="w-4 h-4 mr-1" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="denied">Denied</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Request Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <ShieldOff className="w-10 h-10 mb-2" />
              <p className="text-sm">No access requests found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/50">
                    <th className="text-left p-3 font-medium text-gray-600">Investor</th>
                    <th className="text-left p-3 font-medium text-gray-600">Offering</th>
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    <th className="text-left p-3 font-medium text-gray-600">Requested</th>
                    <th className="text-left p-3 font-medium text-gray-600">Link Expires</th>
                    <th className="text-right p-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(req => (
                    <tr key={req.id} className="border-b last:border-0 hover:bg-gray-50/50">
                      <td className="p-3">
                        <div className="font-medium text-gray-900">
                          {req.user_first_name} {req.user_last_name}
                        </div>
                        <div className="text-xs text-gray-500">{req.user_email}</div>
                      </td>
                      <td className="p-3 text-gray-700">{req.deal_name || req.deal_slug || '—'}</td>
                      <td className="p-3">{statusBadge(req.status)}</td>
                      <td className="p-3 text-gray-500 text-xs">{fmtDate(req.created_at)}</td>
                      <td className="p-3 text-xs">
                        {req.invest_link_expires_at ? (
                          new Date(req.invest_link_expires_at) < new Date()
                            ? <span className="text-red-500">Expired</span>
                            : <span className="text-green-600">{fmtDateTime(req.invest_link_expires_at)}</span>
                        ) : '—'}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openAction(req, 'view')}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {req.status === 'pending' && (
                            <>
                              <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700"
                                onClick={() => openAction(req, 'approve')}>
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700"
                                onClick={() => openAction(req, 'deny')}>
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {req.status === 'approved' && (
                            <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700"
                              onClick={() => openAction(req, 'resend')}>
                              <MailPlus className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={!!actionRequest} onOpenChange={(open) => { if (!open) { setActionRequest(null); setActionType(null); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'view' && 'Request Details'}
              {actionType === 'approve' && 'Approve Access Request'}
              {actionType === 'deny' && 'Deny Access Request'}
              {actionType === 'resend' && 'Resend Invest Link'}
            </DialogTitle>
          </DialogHeader>

          {actionRequest && (
            <div className="space-y-4">
              {/* Request Info */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Investor</span>
                  <span className="font-medium">{actionRequest.user_first_name} {actionRequest.user_last_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium">{actionRequest.user_email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Offering</span>
                  <span className="font-medium">{actionRequest.deal_name || actionRequest.deal_slug}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  {statusBadge(actionRequest.status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Requested</span>
                  <span>{fmtDateTime(actionRequest.created_at)}</span>
                </div>
                {actionRequest.reviewed_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Reviewed</span>
                    <span>{fmtDateTime(actionRequest.reviewed_at)}</span>
                  </div>
                )}
                {actionRequest.invest_link_sent_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Link Sent</span>
                    <span>{fmtDateTime(actionRequest.invest_link_sent_at)}</span>
                  </div>
                )}
                {actionRequest.invest_link_expires_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Link Expires</span>
                    <span>{fmtDateTime(actionRequest.invest_link_expires_at)}</span>
                  </div>
                )}
              </div>

              {/* Investor's message */}
              {actionRequest.request_message && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs font-medium text-gray-500 mb-1">Investor's Message</div>
                  <div className="text-sm text-gray-700">{actionRequest.request_message}</div>
                </div>
              )}

              {/* Existing admin notes */}
              {actionRequest.admin_notes && actionType === 'view' && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-xs font-medium text-blue-600 mb-1">Admin Notes</div>
                  <div className="text-sm text-gray-700">{actionRequest.admin_notes}</div>
                </div>
              )}

              {/* Admin notes input for actions */}
              {actionType && actionType !== 'view' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes {actionType !== 'approve' ? '' : '(optional)'}
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    placeholder={
                      actionType === 'approve' ? 'Optional note for internal records...' :
                      actionType === 'deny' ? 'Reason for denial...' :
                      'Note for resend...'
                    }
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionRequest(null); setActionType(null); }}>
              {actionType === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {actionType === 'approve' && (
              <Button onClick={handleAction} disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700 text-white">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Approve & Send Link
              </Button>
            )}
            {actionType === 'deny' && (
              <Button onClick={handleAction} disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700 text-white">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                Deny Request
              </Button>
            )}
            {actionType === 'resend' && (
              <Button onClick={handleAction} disabled={actionLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MailPlus className="w-4 h-4 mr-1" />}
                Resend Link
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAccessRequests;
